class CookieBanner extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      activeClass: 'is-active',
      closingClass: 'is-closing'
    };
    this.banner = this.querySelector('.cookie-banner');
    // Cookie that records the visitor dismissed the banner, so it doesn't
    // reappear on every page/visit.
    this.consentCookie = 'am_cookie_consent';

    if (this.dataset.testMode === 'true') {
      this.init();

      this.querySelectorAll('.button').forEach(
        (button) => button.addEventListener('click', this.close.bind(this))
      );
    }
    else {
      // AVIATOR-257: the stock banner only showed when Shopify's Customer
      // Privacy / consent-tracking API said consent was required. That API is
      // not active on this store (no consent region configured in Admin), so
      // the banner never appeared for anyone. We now show it to EVERY visitor
      // as a general notice, remembering their choice in a first-party cookie.
      // Shopify's consent API is still used to record the actual tracking
      // consent when it happens to be available, but it's no longer required
      // for the banner to display.
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        () => { /* ignore load errors — banner no longer depends on this */ }
      );

      this.beforeInit();
    }
  }

  hasDismissed() {
    try {
      return document.cookie.split('; ').some((c) => c.indexOf(this.consentCookie + '=') === 0);
    } catch (e) {
      return false;
    }
  }

  rememberDismissed() {
    try {
      // Remember for 1 year.
      var oneYear = 60 * 60 * 24 * 365;
      document.cookie = this.consentCookie + '=1; path=/; max-age=' + oneYear + '; SameSite=Lax';
    } catch (e) {
      /* ignore */
    }
  }

  connectedCallback() {
    if (Shopify.designMode) {
      this.onShopifySectionLoad = this.onSectionLoad.bind(this);
      this.onShopifySectionSelect = this.onSectionSelect.bind(this);
      this.onShopifySectionDeselect = this.onSectionDeselect.bind(this);
      document.addEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }
  disconnectedCallback() {
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.onShopifySectionLoad);
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
    }
  }
  onSectionLoad(event) {
    filterShopifyEvent(event, this, () => this.open.bind(this));
  }
  onSectionSelect(event) {
    filterShopifyEvent(event, this, this.open.bind(this));
  }
  onSectionDeselect(event) {
    filterShopifyEvent(event, this, this.close.bind(this));
  }

  beforeInit() {
    // Show to every visitor who hasn't already dismissed it, except on the
    // cart page. No dependency on Shopify's consent API being active.
    const onCart = window.location.pathname.match(/^(\/[a-z]{2}(-[A-Z]{2})?)?\/cart$/);

    if (!onCart && !this.hasDismissed()) {
      this.init();

      this.querySelectorAll('.button').forEach(
        (button) => button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }
  }

  init() {
    if (Shopify && Shopify.designMode) {
      return;
    }

    setTimeout(function() {
      this.open();
    }.bind(this), parseInt(this.dataset.delay) * 1000);
  }

  open() {
    // AVIATOR-257: the host <cookie-banner> ships with class="hidden", and the
    // theme's base.css has `.hidden { display: none !important }` which overrides
    // the cookie-banner stylesheet — so the host is genuinely display:none. The
    // stock theme only removed `hidden` inside the Shopify consent-API flow, which
    // never runs on this store. Remove it here so the banner can actually show.
    // (The inner `.cookie-banner` is position:fixed, so the host's own display
    // value doesn't matter once it's no longer `none`.)
    this.classList.remove('hidden');
    this.banner.classList.add(this.classes.activeClass);
  }

  close() {
    this.banner.classList.add(this.classes.closingClass);

    setTimeout(() => {
      this.banner.classList.remove(this.classes.activeClass);
      this.banner.classList.remove(this.classes.closingClass);
    }, 500);
  }

  onButtonClick(event) {
    event.preventDefault();
    this.close();
    this.rememberDismissed();

    // Record the actual tracking consent with Shopify when the API is
    // available; if it isn't, the banner is just a notice and we simply
    // remember that it was dismissed (via the cookie above).
    const accepted = event.target.name === 'accept';
    try {
      if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
        window.Shopify.customerPrivacy.setTrackingConsent(accepted, this.noop);
      }
    } catch (e) {
      /* consent API not available — cookie already remembers the choice */
    }
  }

  noop() {
  }
}
customElements.define('cookie-banner', CookieBanner);
